import { useCallback, useEffect, useMemo, useState } from 'react'
import { GlobalState } from '@leverege/ui-redux'
import NextSATriggerHandler from '../queryTriggers/NextSATriggerHandler'

const filter = {
  conditions : [
    {
      field : 'data/progress/status',
      not : true,
      type : 'equals',
      value : 'complete'
    },
    {
      field : 'data/progress/status',
      not : true,
      type : 'equals',
      value : 'skipped'
    }
  ],
  operator : 'and',
  type : 'logical'
}

const sort = [
  {
    field : 'data/order/laserLayerExpectedStep',
    missing : '_first',
    order : 'asc'
  },
  {
    field : 'data/progress/startedAt',
    order : 'desc'
  }
]

export default function useNextSAData( {
  delegate,
  onDataChange
} ) {
  const [ nextSAData, setNextSAData ] = useState( [] )
  const [ hasNextSA, setHasNextSA ] = useState( false )
  const [ nextSADelegate, setNextSADelegate ] = useState( null )
  
  useEffect( () => {
    const nextSALayersDelegate = delegate.child( 'nextSubAssemblyJob' ).child( 'plyLayers' )
    setNextSADelegate( nextSALayersDelegate )
  }, [ delegate ] )
  
  const checkIfNextSAExists = async () => {
    try {
      const shellData = await GlobalState.dispatch( delegate.get() )
      return !!shellData?.data?.nextSubAssemblyJob?.id
    } catch ( err ) {
      console.error( 'Error checking for nextSubAssemblyJob:', err )
      return false
    }
  }
  
  const fetchData = async () => {
    if ( !nextSADelegate ) return
    
    try {
      const results = await GlobalState.dispatch(
        nextSADelegate.search( {
          filter,
          sort
        }, {
          clearOnError : true
        } )
      )
      
      const items = results?.items || []
      
      if ( items.length === 0 ) {
        setNextSAData( [] )
        if ( onDataChange ) await onDataChange( false, [] )
        return
      }
      
      const processedData = items.map( ( item ) => {
        const layerId = item.id
        const detail = nextSADelegate.itemRef( layerId )
        return {
          data : item,
          id : detail.id,
          ref : detail.ref,
          type : detail.type,
        }
      } )
      
      setNextSAData( processedData )
      if ( onDataChange ) await onDataChange( true, processedData )
    } catch ( err ) {
      console.error( 'Error fetching next SA data:', err )
      setNextSAData( [] )
      if ( onDataChange ) await onDataChange( false, [] )
    }
  }
  
  const checkForNextSA = useCallback( async () => {
    const nextSAExists = await checkIfNextSAExists()
    
    if ( nextSAExists !== hasNextSA ) {
      setHasNextSA( nextSAExists )
      
      if ( !nextSAExists && nextSAData.length > 0 ) {
        setNextSAData( [] )
        if ( onDataChange ) await onDataChange( false, [] )
      } else if ( nextSAExists ) {
        await fetchData()
      }
    }
    
    return nextSAExists
  }, [ delegate, hasNextSA, nextSAData.length, nextSADelegate, onDataChange ] )
  
  const fetchNextSAData = useCallback( async () => {
    const nextSAExists = await checkIfNextSAExists()
    
    setHasNextSA( nextSAExists )
    
    if ( !nextSAExists ) {
      setNextSAData( [] )
      if ( onDataChange ) await onDataChange( false, [] )
      return
    }
    
    await fetchData()
  }, [ nextSADelegate, onDataChange ] )

  const context = useMemo( () => {
    return {
      clientProps : {
        delegate : nextSADelegate
      }
    }
  }, [ nextSADelegate ] )

  const trigger = useMemo( () => {
    if ( !nextSADelegate ) return null

    const t = new NextSATriggerHandler( {
      delegate : nextSADelegate,
      shellDelegate : delegate,
      path : nextSADelegate?.relationship?.path,
      client : 'GroupScreen',
      loadData : fetchNextSAData
    }, context )

    return t
  }, [ context, nextSADelegate, fetchNextSAData, delegate ] )

  useEffect( () => {
    if ( trigger ) {
      trigger.init()
      
      return () => {
        if ( trigger ) {
          trigger.cleanup()
        }
      }
    }
    
    return undefined
  }, [ trigger ] )
  
  useEffect( () => {
    if ( nextSADelegate ) {
      checkForNextSA()
    }
  }, [ nextSADelegate, checkForNextSA ] )

  return {
    nextSAData,
    hasNextSA,
    nextSADelegate,
    checkForNextSA
  }
}
